import { Fragment, useState } from 'react';

import {
  useAffinityListConfigs,
  useAffinityListFields,
  useCreateListConfig,
  useCreateListCustomField,
  useDeleteListConfig,
  useDeleteListCustomField,
  useListCustomFields,
  useUpdateListConfig,
  useUpdateListCustomField,
} from '../../hooks/useOutreach';
import {
  CAMPAIGN_EVENTS,
  CAMPAIGN_EVENT_LABELS,
} from '../../types/outreach';
import type {
  AffinityListConfig,
  AffinityListCustomField,
  AffinityListField,
  CustomFieldValueType,
  EventStatusMap,
} from '../../types/outreach';
import styles from '../pages/OutreachPage.module.css';

const VARIABLE_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** crm-sourced configs are served by a native CRM list, not Affinity. */
function isNative(c: AffinityListConfig): boolean {
  return c.source_kind === 'crm';
}

/**
 * List-config sub-view: register Affinity people lists with their
 * status-field mapping + suppression fields. The "Discover fields" button
 * pulls dropdown options live from Affinity V1 /fields and prefills the
 * cache so admins don't type option IDs by hand.
 *
 * A config whose source has been flipped to the native CRM list renders
 * read-mostly instead: its field bindings are owned by the audience
 * import, so the Affinity field-id editors and discovery are hidden and
 * only the list-level settings (label, slug, event map, terminal/paused
 * labels, response URL) stay editable.
 */
export function ListConfigsAdmin() {
  const configsQ = useAffinityListConfigs();
  const [editing, setEditing] = useState<AffinityListConfig | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>List configs</h2>
        {!creating && !editing && (
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={() => setCreating(true)}
          >
            + Register list
          </button>
        )}
      </div>

      {creating && (
        <ListConfigForm
          mode="create"
          onClose={() => setCreating(false)}
        />
      )}

      {editing && (
        <ListConfigForm
          mode="edit"
          existing={editing}
          onClose={() => setEditing(null)}
        />
      )}

      {!creating && !editing && (
        <ListConfigsTable
          configs={configsQ.data || []}
          isLoading={configsQ.isLoading}
          onEdit={setEditing}
        />
      )}
    </div>
  );
}


function ListConfigsTable({
  configs,
  isLoading,
  onEdit,
}: {
  configs: AffinityListConfig[];
  isLoading: boolean;
  onEdit: (c: AffinityListConfig) => void;
}) {
  const deleteMut = useDeleteListConfig();

  async function handleDelete(c: AffinityListConfig) {
    if (
      !window.confirm(
        `Delete list config for ${c.label || c.affinity_list_id}? ` +
          `Campaigns still pointing at it block the delete — re-point or ` +
          `delete those first.`,
      )
    ) {
      return;
    }
    try {
      await deleteMut.mutateAsync(c.affinity_list_id);
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Delete failed.');
    }
  }

  if (isLoading) return <div className={styles.empty}>Loading…</div>;
  if (configs.length === 0) {
    return (
      <div className={styles.empty}>
        No lists registered yet. Click <strong>Register list</strong>{' '}
        to add one.
      </div>
    );
  }

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>List ID</th>
          <th>Source</th>
          <th>Label</th>
          <th>Slug</th>
          <th>Status field</th>
          <th>Events mapped</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {configs.map((c) => (
          <tr key={c.affinity_list_id}>
            <td className={styles.code}>{c.affinity_list_id}</td>
            <td>
              <span className={`${styles.statusBadge} ${styles.statusInactive}`}>
                {isNative(c) ? 'native' : 'affinity'}
              </span>
              {isNative(c) && c.crm_list_id != null && (
                <span className={styles.code} style={{ marginLeft: 6 }}>
                  crm:{c.crm_list_id}
                </span>
              )}
            </td>
            <td>{c.label || <em style={{ color: '#999' }}>—</em>}</td>
            <td className={styles.code}>{c.list_slug}</td>
            <td className={styles.code}>
              {isNative(c) ? (c.crm_status_field_key || '—') : c.status_field_id}
            </td>
            <td>
              {Object.values(c.event_to_status_map).filter((v) => v != null).length}
              {' / '}
              {Object.keys(c.event_to_status_map).length}
            </td>
            <td>
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => onEdit(c)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className={`${styles.button} ${styles.buttonDanger}`}
                  onClick={() => handleDelete(c)}
                  disabled={deleteMut.isPending}
                >
                  Delete
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


/**
 * Read-only summary of a crm-sourced config's native bindings: which CRM
 * list serves it and which field keys carry status / scope / dietary /
 * comments. These are set by the audience import + the campaign
 * migration transform, never typed in here.
 */
function NativeSourcePanel({ config }: { config: AffinityListConfig }) {
  const rows: [string, string][] = [
    ['CRM list id', config.crm_list_id != null ? String(config.crm_list_id) : '—'],
    ['Status field key', config.crm_status_field_key || '—'],
    ['Scope field key', config.crm_scope_field_key || '— (no scope axis)'],
    ['Dietary field key', config.crm_dietary_field_key || '—'],
    ['Comments field key', config.crm_comments_field_key || '—'],
  ];
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ marginBottom: 6 }}>
        <span className={`${styles.statusBadge} ${styles.statusInactive}`}>
          native list
        </span>
      </div>
      <div className={styles.helpText} style={{ marginBottom: 8 }}>
        This list is served by a native CRM list. Its field bindings are
        owned by the audience import, so they're read-only here — the
        label, slug, event → status map, terminal / paused labels and
        response URL stay editable.
      </div>
      <div className={styles.formGrid}>
        {rows.map(([k, v]) => (
          <Fragment key={k}>
            <label className={styles.label}>{k}</label>
            <div className={styles.code}>{v}</div>
          </Fragment>
        ))}
      </div>
    </div>
  );
}


type ListConfigFormProps =
  | { mode: 'create'; onClose: () => void; existing?: never }
  | { mode: 'edit'; onClose: () => void; existing: AffinityListConfig };


function ListConfigForm(props: ListConfigFormProps) {
  const isEdit = props.mode === 'edit';
  const init = props.existing;
  const createMut = useCreateListConfig();
  const updateMut = useUpdateListConfig();

  const [listId, setListId] = useState<string>(
    init ? String(init.affinity_list_id) : '',
  );
  const [label, setLabel] = useState(init?.label || '');
  const [slug, setSlug] = useState(init?.list_slug || '');
  const [statusFieldId, setStatusFieldId] = useState<string>(
    init ? String(init.status_field_id) : '',
  );
  const [dropdownOptions, setDropdownOptions] = useState<Record<string, number>>(
    init?.status_dropdown_options || {},
  );
  const [eventMap, setEventMap] = useState<EventStatusMap>(() => {
    if (init) return { ...init.event_to_status_map };
    const seed: EventStatusMap = {};
    for (const ev of CAMPAIGN_EVENTS) seed[ev] = null;
    return seed;
  });
  const [terminal, setTerminal] = useState<string[]>(
    init?.terminal_status_values || [],
  );
  const [paused, setPaused] = useState<string[]>(
    init?.paused_status_values || [],
  );
  const [suppressionFieldId, setSuppressionFieldId] = useState<string>(
    init?.suppression_field_id ? String(init.suppression_field_id) : '',
  );
  const [suppressionDropdownOptionId, setSuppressionDropdownOptionId] = useState<string>(
    init?.suppression_dropdown_option_id
      ? String(init.suppression_dropdown_option_id)
      : '',
  );
  const [suppressionReasonFieldId, setSuppressionReasonFieldId] = useState<string>(
    init?.suppression_reason_field_id ? String(init.suppression_reason_field_id) : '',
  );
  const [dietaryFieldId, setDietaryFieldId] = useState<string>(
    init?.dietary_field_id ? String(init.dietary_field_id) : '',
  );
  const [commentsFieldId, setCommentsFieldId] = useState<string>(
    init?.comments_field_id ? String(init.comments_field_id) : '',
  );
  const [responseUrlTemplate, setResponseUrlTemplate] = useState(
    init?.response_url_template || '',
  );
  // Scope filter slot — second filter axis. Optional. Mirrors the
  // status-field machinery above (field id + cached options) so the
  // campaign-creation UI can render a multi-select on top of it.
  const [scopeFieldId, setScopeFieldId] = useState<string>(
    init?.scope_field_id ? String(init.scope_field_id) : '',
  );
  const [scopeFieldName, setScopeFieldName] = useState<string>(
    init?.scope_field_name || '',
  );
  const [scopeDropdownOptions, setScopeDropdownOptions] = useState<
    Record<string, number>
  >(init?.scope_dropdown_options || {});
  const [error, setError] = useState<string | null>(null);

  // A crm-sourced config's field bindings belong to the native list (the
  // audience import owns them) — no Affinity editors, no discovery.
  const native = isEdit && !!init && isNative(init);

  // Live-fetch fields when the user has a numeric list ID. Never for a
  // native config: the backend refuses discovery on those, and there is
  // nothing on Affinity to discover.
  const numericListId = parseInt(listId, 10);
  const fieldsQ = useAffinityListFields(
    !native && Number.isFinite(numericListId) && numericListId > 0
      ? numericListId
      : null,
  );

  const dropdownLabels = Object.keys(dropdownOptions);

  function applyDiscoveredField(field: AffinityListField) {
    setStatusFieldId(String(field.id));
    const optMap: Record<string, number> = {};
    for (const opt of field.dropdown_options) {
      if (opt.text) optMap[opt.text] = opt.id;
    }
    setDropdownOptions(optMap);
  }

  function applyDiscoveredScopeField(field: AffinityListField) {
    setScopeFieldId(String(field.id));
    setScopeFieldName(field.name);
    const optMap: Record<string, number> = {};
    for (const opt of field.dropdown_options) {
      if (opt.text) optMap[opt.text] = opt.id;
    }
    setScopeDropdownOptions(optMap);
  }

  function refreshScopeOptions() {
    // Pull the latest dropdown options from Affinity for the scope
    // field. The "Refresh options" button is the only way the cache
    // gets repopulated after the initial pick — there's no scheduled
    // sync. Re-uses the V1 /fields proxy that already powers the
    // status-field discovery above.
    const targetId = parseInt(scopeFieldId, 10);
    if (!Number.isFinite(targetId) || targetId <= 0) return;
    if (!fieldsQ.data) return;
    const found = fieldsQ.data.find((f) => f.id === targetId);
    if (!found) {
      setError(
        `Scope field id=${targetId} not found in current Affinity field list.`,
      );
      return;
    }
    applyDiscoveredScopeField(found);
  }

  function setEventMapping(event: string, value: string) {
    setEventMap((prev) => ({ ...prev, [event]: value || null }));
  }

  function toggleInArray(arr: string[], val: string): string[] {
    return arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
  }

  async function handleSave() {
    setError(null);
    const id = parseInt(listId, 10);
    const sfid = parseInt(statusFieldId, 10);
    if (!Number.isFinite(id) || id <= 0) {
      setError('List ID must be a positive integer.');
      return;
    }
    if (!native && (!Number.isFinite(sfid) || sfid <= 0)) {
      setError('Status field ID must be a positive integer.');
      return;
    }
    if (!slug.match(/^[a-z0-9][a-z0-9-]{0,62}$/)) {
      setError('Slug must be lowercase, alphanumeric + hyphens only.');
      return;
    }

    const optionalInt = (v: string): number | null => {
      const n = parseInt(v, 10);
      return Number.isFinite(n) && n > 0 ? n : null;
    };

    try {
      if (native) {
        // Only the list-level settings are editable on a native config;
        // every Affinity field-id slot is left untouched (omitted keys
        // are no-ops server-side).
        await updateMut.mutateAsync({
          listId: id,
          body: {
            label: label || null,
            list_slug: slug,
            event_to_status_map: eventMap,
            terminal_status_values: terminal,
            paused_status_values: paused,
            response_url_template: responseUrlTemplate || null,
          },
        });
      } else if (isEdit) {
        await updateMut.mutateAsync({
          listId: id,
          body: {
            label: label || null,
            list_slug: slug,
            status_field_id: sfid,
            status_dropdown_options: dropdownOptions,
            event_to_status_map: eventMap,
            terminal_status_values: terminal,
            paused_status_values: paused,
            suppression_field_id: optionalInt(suppressionFieldId),
            suppression_dropdown_option_id: optionalInt(suppressionDropdownOptionId),
            suppression_reason_field_id: optionalInt(suppressionReasonFieldId),
            dietary_field_id: optionalInt(dietaryFieldId),
            // 0 means "clear the slot" — backend mirrors the scope
            // convention here so blanking a previously-set value works.
            comments_field_id: optionalInt(commentsFieldId) ?? 0,
            // 0 means "clear the slot". Backend treats 0 / negative the
            // same as "scope filter not configured."
            scope_field_id: optionalInt(scopeFieldId) ?? 0,
            scope_field_name: scopeFieldName || null,
            scope_dropdown_options: scopeDropdownOptions,
            response_url_template: responseUrlTemplate || null,
          },
        });
      } else {
        await createMut.mutateAsync({
          affinity_list_id: id,
          label: label || null,
          list_slug: slug,
          status_field_id: sfid,
          status_dropdown_options: dropdownOptions,
          event_to_status_map: eventMap,
          terminal_status_values: terminal,
          paused_status_values: paused,
          suppression_field_id: optionalInt(suppressionFieldId),
          suppression_reason_field_id: optionalInt(suppressionReasonFieldId),
          dietary_field_id: optionalInt(dietaryFieldId),
          comments_field_id: optionalInt(commentsFieldId),
          scope_field_id: optionalInt(scopeFieldId),
          scope_field_name: scopeFieldName || null,
          scope_dropdown_options: scopeDropdownOptions,
          response_url_template: responseUrlTemplate || null,
        });
      }
      props.onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    }
  }

  const busy = createMut.isPending || updateMut.isPending;

  return (
    <div style={{ marginBottom: 16, padding: 12, background: '#FAF9F4', borderRadius: 6 }}>
      <h3 style={{ margin: '0 0 12px', fontFamily: 'DM Sans, sans-serif', fontSize: 14 }}>
        {isEdit ? `Edit list ${init?.affinity_list_id}` : 'Register an Affinity list'}
      </h3>
      {error && <div className={styles.error}>{error}</div>}

      {native && init && <NativeSourcePanel config={init} />}

      <div className={styles.formGrid}>
        <label className={styles.label}>
          {native ? 'List ID' : 'Affinity list ID'}
        </label>
        <input
          className={styles.input}
          type="number"
          placeholder="346659"
          value={listId}
          onChange={(e) => setListId(e.target.value)}
          disabled={isEdit}
        />

        <label className={styles.label}>Label</label>
        <input
          className={styles.input}
          type="text"
          placeholder="AGM 2026"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />

        <label className={styles.label}>Slug</label>
        <input
          className={styles.input}
          type="text"
          placeholder="agm-2026"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
        />

        {!native && (
          <>
            <label className={styles.label}>Status field ID</label>
            <input
              className={styles.input}
              type="number"
              value={statusFieldId}
              onChange={(e) => setStatusFieldId(e.target.value)}
            />
          </>
        )}

        <label className={styles.label}>Response URL template</label>
        <input
          className={styles.input}
          type="text"
          placeholder="https://connect.ember-infra.com/r/agm-2026/{token}"
          value={responseUrlTemplate}
          onChange={(e) => setResponseUrlTemplate(e.target.value)}
        />
      </div>

      {/* Field discovery */}
      {fieldsQ.data && fieldsQ.data.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className={styles.label} style={{ marginBottom: 6 }}>
            Discovered fields on list {numericListId}:
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {fieldsQ.data
              .filter((f) => f.dropdown_options.length > 0)
              .map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={styles.button}
                  onClick={() => applyDiscoveredField(f)}
                  title={`${f.dropdown_options.length} options`}
                >
                  Use “{f.name}” ({f.id})
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Event-to-status mapping */}
      <div style={{ marginTop: 16 }}>
        <div className={styles.label} style={{ marginBottom: 6 }}>
          Event → status mapping
        </div>
        <table className={styles.eventMappingTable}>
          <thead>
            <tr>
              <th>Event</th>
              <th>Maps to status</th>
              <th>Terminal</th>
              <th>Paused</th>
            </tr>
          </thead>
          <tbody>
            {CAMPAIGN_EVENTS.map((ev) => (
              <tr key={ev}>
                <td>
                  <div className={styles.eventName}>{ev}</div>
                  <div className={styles.helpText}>{CAMPAIGN_EVENT_LABELS[ev]}</div>
                </td>
                <td>
                  <select
                    className={styles.input}
                    value={eventMap[ev] || ''}
                    onChange={(e) => setEventMapping(ev, e.target.value)}
                  >
                    <option value="">— no automatic update —</option>
                    {dropdownLabels.map((label) => (
                      <option key={label} value={label}>
                        {label}
                      </option>
                    ))}
                  </select>
                </td>
                <td />
                <td />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Terminal + paused checkboxes */}
      {dropdownLabels.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className={styles.label} style={{ marginBottom: 6 }}>
            Status flags
          </div>
          <table className={styles.eventMappingTable}>
            <thead>
              <tr>
                <th>Status label</th>
                <th>Terminal (won't auto-overwrite)</th>
                <th>Paused (excludes from sending)</th>
              </tr>
            </thead>
            <tbody>
              {dropdownLabels.map((label) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={terminal.includes(label)}
                      onChange={() => setTerminal(toggleInArray(terminal, label))}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={paused.includes(label)}
                      onChange={() => setPaused(toggleInArray(paused, label))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Suppression fields — Affinity field ids; a native list's
          equivalents are owned by the audience import. */}
      {!native && (
      <>
      <div className={styles.formGrid} style={{ marginTop: 12 }}>
        <label className={styles.label}>Suppression field ID</label>
        <input
          className={styles.input}
          type="number"
          placeholder="optional"
          value={suppressionFieldId}
          onChange={(e) => setSuppressionFieldId(e.target.value)}
        />
        <label className={styles.label}>“Do Not Contact” option ID</label>
        <input
          className={styles.input}
          type="number"
          placeholder="dropdown option ID for the suppression value"
          value={suppressionDropdownOptionId}
          onChange={(e) => setSuppressionDropdownOptionId(e.target.value)}
        />
        <label className={styles.label}>Suppression reason field ID</label>
        <input
          className={styles.input}
          type="number"
          placeholder="optional"
          value={suppressionReasonFieldId}
          onChange={(e) => setSuppressionReasonFieldId(e.target.value)}
        />
        <label className={styles.label}>Dietary field ID</label>
        <input
          className={styles.input}
          type="number"
          placeholder="optional"
          value={dietaryFieldId}
          onChange={(e) => setDietaryFieldId(e.target.value)}
        />
        <label className={styles.label}>Comments field ID</label>
        <input
          className={styles.input}
          type="number"
          placeholder="optional"
          value={commentsFieldId}
          onChange={(e) => setCommentsFieldId(e.target.value)}
        />
      </div>
      <div className={styles.helpText} style={{ marginTop: 4 }}>
        The suppression field is a single-option dropdown
        (“Do Not Contact”; empty = active). The option ID is what Affinity
        writes when the engine flips a person to suppressed — find it under
        Field settings → dropdown options on the person field. Dietary is
        a person-level text field; Comments is a list-scoped text field
        that the RSVP form overwrites on every submission (latest wins).
      </div>

      {/* Scope filter slot */}
      <div style={{ marginTop: 16 }}>
        <div className={styles.label} style={{ marginBottom: 6 }}>
          Scope filter (optional second filter axis)
        </div>
        <div className={styles.helpText} style={{ marginBottom: 8 }}>
          Pick a list-scoped dropdown / ranked-dropdown / dropdown-multi
          field to give campaigns a second filter axis on top of Status
          (e.g. <em>Campaign</em> on AGM 2026). Click <strong>Refresh
          options</strong> to re-cache the dropdown options after the
          field's options change in Affinity.
        </div>
        <div className={styles.formGrid}>
          <label className={styles.label}>Scope field</label>
          <select
            className={styles.input}
            value={scopeFieldId}
            onChange={(e) => {
              const v = e.target.value;
              setScopeFieldId(v);
              if (!v) {
                setScopeFieldName('');
                setScopeDropdownOptions({});
                return;
              }
              const id = parseInt(v, 10);
              const f = (fieldsQ.data || []).find((x) => x.id === id);
              if (f) applyDiscoveredScopeField(f);
            }}
          >
            <option value="">— no scope filter —</option>
            {(fieldsQ.data || [])
              .filter((f) => f.dropdown_options.length > 0)
              .map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name} ({f.id})
                </option>
              ))}
          </select>
          <label className={styles.label}>Cached options</label>
          <div>
            <span className={styles.code}>
              {Object.keys(scopeDropdownOptions).length} option(s)
              {scopeFieldName ? ` for ${scopeFieldName}` : ''}
            </span>
            <button
              type="button"
              className={styles.button}
              onClick={refreshScopeOptions}
              disabled={!scopeFieldId || !fieldsQ.data}
              style={{ marginLeft: 8 }}
              title="Re-fetch this scope field's dropdown options from Affinity"
            >
              Refresh options
            </button>
          </div>
        </div>
      </div>
      </>
      )}

      {/* Custom merge fields — only available once the list config
          exists; create-mode admins save first, then re-open in edit.
          A native list declares its merge variables through the audience
          import (the backend refuses Affinity-field-id CRUD on it). */}
      {isEdit && init && !native && (
        <ListCustomFieldsManager
          listId={init.affinity_list_id}
          availableFields={fieldsQ.data || []}
        />
      )}
      {native && (
        <div className={styles.helpText} style={{ marginTop: 16 }}>
          Custom merge fields on a native list are declared by the
          audience import, not here.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
        <button type="button" className={styles.button} onClick={props.onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={`${styles.button} ${styles.buttonPrimary}`}
          onClick={handleSave}
          disabled={busy}
        >
          {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create config'}
        </button>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Custom merge fields manager (Feature 2)
// ---------------------------------------------------------------------------

/**
 * Inline manager rendered inside the list-config edit form. Lets admins
 * declare `{{VariableName}} → Affinity field` mappings on the list.
 * Recipient sync pulls the field's value per recipient; templates on
 * any campaign for this list can reference the variable.
 */
function ListCustomFieldsManager({
  listId,
  availableFields,
}: {
  listId: number;
  availableFields: AffinityListField[];
}) {
  const cfsQ = useListCustomFields(listId);
  const createMut = useCreateListCustomField();
  const updateMut = useUpdateListCustomField();
  const deleteMut = useDeleteListCustomField();

  // New-row drafting state
  const [draftVar, setDraftVar] = useState('');
  const [draftFieldId, setDraftFieldId] = useState<string>('');
  const [draftType, setDraftType] = useState<CustomFieldValueType>('text');
  const [draftFallback, setDraftFallback] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Multi-value fields aren't supported as custom merge fields (would
  // require array merge semantics + Jinja loops in templates, both
  // out of scope). Filter them out of the picker so admins can't pick
  // an unsupported field and hit a 400 on save.
  const singleValueFields = availableFields.filter(
    (f) => !f.allows_multiple,
  );
  const existing = cfsQ.data || [];
  const usedFieldIds = new Set(existing.map((e) => e.affinity_field_id));

  async function handleCreate() {
    setError(null);
    if (!VARIABLE_NAME_RE.test(draftVar)) {
      setError(
        'Variable name must start with a letter or underscore and use ' +
        'only letters, digits, and underscores — no spaces.',
      );
      return;
    }
    const fid = parseInt(draftFieldId, 10);
    if (!Number.isFinite(fid) || fid <= 0) {
      setError('Pick an Affinity field.');
      return;
    }
    try {
      await createMut.mutateAsync({
        listId,
        body: {
          variable_name: draftVar.trim(),
          affinity_field_id: fid,
          value_type: draftType,
          fallback_text: draftFallback,
        },
      });
      setDraftVar('');
      setDraftFieldId('');
      setDraftType('text');
      setDraftFallback('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Create failed.');
    }
  }

  async function handleDelete(cf: AffinityListCustomField) {
    if (!window.confirm(
      `Remove custom merge field {{${cf.variable_name}}}? ` +
      `Any step templates that reference it will be checked first.`,
    )) return;
    setError(null);
    try {
      await deleteMut.mutateAsync({ listId, cfId: cf.id });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Delete failed.');
    }
  }

  async function handleUpdateFallback(cf: AffinityListCustomField, val: string) {
    if (val === cf.fallback_text) return;
    try {
      await updateMut.mutateAsync({
        listId, cfId: cf.id,
        body: { fallback_text: val },
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Update failed.');
    }
  }

  return (
    <div
      style={{
        marginTop: 16, padding: 12,
        background: '#FFFFFF', border: '1px solid #E5E2D9',
        borderRadius: 6,
      }}
    >
      <div
        style={{
          display: 'flex', alignItems: 'baseline',
          justifyContent: 'space-between', marginBottom: 8,
        }}
      >
        <h3
          style={{
            margin: 0, fontFamily: 'DM Sans, sans-serif',
            fontSize: 14, fontWeight: 600,
          }}
        >
          Custom merge fields
        </h3>
        <span style={{ color: '#888', fontSize: 12 }}>
          Available in every campaign on this list as <code>{'{{VarName}}'}</code>
        </span>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {cfsQ.isLoading && (
        <div className={styles.empty}>Loading custom fields…</div>
      )}
      {!cfsQ.isLoading && existing.length === 0 && (
        <div className={styles.empty} style={{ marginBottom: 8 }}>
          No custom merge fields declared yet. Add one below to make a per-
          recipient Affinity field available as <code>{'{{VarName}}'}</code> in
          step templates.
        </div>
      )}
      {existing.length > 0 && (
        <table className={styles.table} style={{ marginBottom: 12 }}>
          <thead>
            <tr>
              <th>Variable</th>
              <th>Affinity field</th>
              <th>Type</th>
              <th>Fallback</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {existing.map((cf) => (
              <tr key={cf.id}>
                <td className={styles.code}>{`{{${cf.variable_name}}}`}</td>
                <td>
                  {cf.affinity_field_name}{' '}
                  <span style={{ color: '#888', fontSize: 11 }}>
                    (id={cf.affinity_field_id})
                  </span>
                </td>
                <td>{cf.value_type}</td>
                <td>
                  <input
                    className={styles.input}
                    style={{ width: 180 }}
                    type="text"
                    defaultValue={cf.fallback_text}
                    placeholder="(empty)"
                    onBlur={(e) => handleUpdateFallback(cf, e.target.value)}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className={`${styles.button} ${styles.buttonDanger}`}
                    onClick={() => handleDelete(cf)}
                    disabled={deleteMut.isPending}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Draft new mapping */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr 1fr 1fr auto',
          gap: 8, alignItems: 'end',
        }}
      >
        <div>
          <div className={styles.label} style={{ marginBottom: 4 }}>
            Variable name
          </div>
          <input
            className={styles.input}
            type="text"
            placeholder="CustomNote"
            value={draftVar}
            onChange={(e) => setDraftVar(e.target.value)}
          />
        </div>
        <div>
          <div className={styles.label} style={{ marginBottom: 4 }}>
            Affinity field
          </div>
          <select
            className={styles.input}
            value={draftFieldId}
            onChange={(e) => setDraftFieldId(e.target.value)}
          >
            <option value="">Pick a field…</option>
            {singleValueFields.map((f) => (
              <option
                key={f.id}
                value={f.id}
                disabled={usedFieldIds.has(f.id)}
              >
                {f.name}
                {usedFieldIds.has(f.id) ? ' (already mapped)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <div className={styles.label} style={{ marginBottom: 4 }}>
            Value type
          </div>
          <select
            className={styles.input}
            value={draftType}
            onChange={(e) =>
              setDraftType(e.target.value as CustomFieldValueType)
            }
          >
            <option value="text">text</option>
            <option value="number">number</option>
            <option value="dropdown">dropdown</option>
            <option value="date">date</option>
          </select>
        </div>
        <div>
          <div className={styles.label} style={{ marginBottom: 4 }}>
            Fallback
          </div>
          <input
            className={styles.input}
            type="text"
            placeholder="(empty)"
            value={draftFallback}
            onChange={(e) => setDraftFallback(e.target.value)}
          />
        </div>
        <button
          type="button"
          className={`${styles.button} ${styles.buttonPrimary}`}
          onClick={handleCreate}
          disabled={createMut.isPending}
        >
          {createMut.isPending ? 'Adding…' : 'Add'}
        </button>
      </div>
      {singleValueFields.length === 0 && (
        <div className={styles.helpText} style={{ marginTop: 6 }}>
          No single-value Affinity fields discovered yet. Make sure the
          list ID above is set and re-open the editor.
        </div>
      )}
    </div>
  );
}
